import React from "react";
import { TFunction } from "i18next";
import { toast } from "react-toastify";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { uuid } from "uuidv4";

import { getData } from "@helpers/general";
import { clone } from "@helpers/clone";
import { CESAR_LOYALTY, numberMatchWord } from "@constants/index";
import {
    FidelityValues,
    FidelityFlags,
    FidelityErrors,
} from "./useFidelityForm";
import { store } from "../../../redux/store";
import {
    setGlobalDataIsUpdated,
    setIsConsultModalOpened,
    setIsLoading,
    setKey,
} from "../../../redux/slices/fidelitySlice";

type ArchiveContext = {
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    t: TFunction;
};

function toError(err: unknown): Error {
    return err instanceof Error ? err : new Error(String(err));
}

export function getArrayOfRows(
    selectedRows: FidelityRow[],
    t: TFunction
): FidelityRow[] {
    const ids = new Set<string>();
    const result: FidelityRow[] = [];

    for (const el of selectedRows) {
        if (el[t("Shop")] !== undefined) continue;

        const key =
            el.fidelityChoiceIndex !== CESAR_LOYALTY
                ? `${el.shopId}${el.Id}`
                : `${el.affectedShop}${el.Id}`;

        if (!ids.has(key)) {
            ids.add(key);
            result.push(el);
        }
    }

    return result;
}

export function buildFidelityPayload(
    el: FidelityRow,
    t: TFunction
): FidelityPayload {
    return {
        designation: el[t("Display name")],
        type: el[t("Loyalty")],
        number_fidelity_points: el.number_fidelity_points,
        money_correspondence: el.moneyCorrespondence,
        devise: el.Devise,
        pays: el.Pays,
        authentication_mode: el.authentication_mode,
        sortedVisibility: el.sortedVisibility,
        Login: el.Login,
        note: el[t("Remark")],
        isActive: false,
        isArchived:
            el.fidelityChoiceIndex !== CESAR_LOYALTY
                ? true
                : JSON.stringify(el.isArchived),
        shopId: el.shopId.toString(),
        dashboardLink: el[t("Dashboard Link")],
        user: el.user,
        auresKonnectGroup: el[t("Group")],
        isCentralizationActive: el[t("State")],
        isVisibiliteActive: el[t("isVisibiliteActive")],
        fidelityChoiceIndex: el.fidelityChoiceIndex,
    };
}

export function getAssociatedIds(shopsData: ShopsFidelityData): string[] {
    const ids = new Set<string>();
    if (shopsData?.data) {
        for (const element of shopsData.data) {
            if (element.subRows) {
                for (const el of element.subRows) {
                    ids.add(el.Id);
                }
            }
        }
    }
    return [...ids];
}

export function getShopField(
    rowsShop: ShopsFidelityShop[],
    shopId: string,
    field: string
): FidelityRow[] {
    const shop = rowsShop.find((elt) => elt.shopId === shopId);
    return clone(shop?.[field] ?? []);
}

export function countMatchingIds(
    selectedRows: FidelityRow[],
    idsLocalShop: string[]
): number {
    const selectedIdSet = new Set(selectedRows.map((v) => v.Id));
    return idsLocalShop.filter((el) => selectedIdSet.has(el)).length;
}

export function getArchivedFidelityIds(
    rowsShop: ShopsFidelityShop[]
): string[] {
    const archived: string[] = [];
    for (const element of rowsShop ?? []) {
        if (element.archivedFidelity !== undefined) {
            for (const el of element.archivedFidelity) {
                archived.push(el.Id);
            }
        }
    }
    return archived;
}

export async function saleModeData(
    userID: number,
    franchiseID: string,
    shopID: string,
    fetchGlobal: FetchGlobal
): Promise<Result<SaleModeVisibility[]>> {
    try {
        const response: any = await fetchGlobal(
            `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_sale?userId=${userID}&franchiseId=${franchiseID}&shopId=${shopID}`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            }
        );
        let saleModes = response.data.map((saleMode: SaleModeApiItem) => {
            return {
                designation: saleMode["Mode of sale"],
                state: false,
                saleSupports: (saleMode.support_vente ?? []).map(
                    (el: string) => {
                        return {
                            designation: el,
                            state: false,
                        };
                    }
                ),
            };
        });

        return { ok: true, value: saleModes };
    } catch (error) {
        return { ok: false, error: toError(error) };
    }
}

export function associateFidelity(
    row: FidelityRow,
    shopsFidelityData: ShopsFidelityData
): FidelityRow[] {
    const dataAssociate: FidelityRow[] = [];
    if (shopsFidelityData.data !== undefined) {
        for (const element of shopsFidelityData.data) {
            if (element.subRows !== undefined) {
                for (const el of element.subRows) {
                    if (el.Id === row.Id && el.shopId !== "") {
                        dataAssociate.push(JSON.parse(JSON.stringify(el)));
                    }
                }
            }
        }
    }
    return dataAssociate;
}

export function mergeArchives(row: FidelityRow, associated: FidelityRow[]) {
    let archives: FidelityArchiveEntry[] = (row.isArchived as FidelityArchiveEntry[]).map(
        (elt) => ({
            ...elt,
            status: true,
        })
    );
    associated.forEach((archive) => {
        archive.isArchived = (archive.isArchived as FidelityArchiveEntry[]).map(
            (item) => ({
                ...item,
                status: true,
            })
        );
        archives = [
            ...archives,
            ...(archive.isArchived as FidelityArchiveEntry[]),
        ];
    });
    row.isArchived = archives.filter(
        (item, index, self) =>
            index === self.findIndex((entry) => entry.shopId === item.shopId)
    );
}

export async function submitArchive(
    ctx: ArchiveContext,
    data: Record<string, FidelityPayload>,
    shopIds: string[],
    extraFields?: Record<string, unknown>
): Promise<Result<{ shopIds: string[] }>> {
    const { userID, franchiseID, operatorID, uuidUser, fetchGlobal, t } = ctx;
    const body = {
        userId: userID,
        shopId: shopIds,
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser,
        isShop: false,
        data,
        ...extraFields,
    };
    const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;
    try {
        const result: any = await fetchGlobal(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (result?.error) {
            store.dispatch(setIsLoading(false));
            return {
                ok: false,
                error: new Error(
                    typeof result.error === "string"
                        ? result.error
                        : "API returned an error"
                ),
            };
        }
        toast.success(
            `${t("The fidelity settings have been successfully archived")}`,
            {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            }
        );
        store.dispatch(setKey(uuid()));
        await getData(franchiseID, uuidUser as string, shopIds, fetchGlobal);
        store.dispatch(setIsLoading(false));
        store.dispatch(setGlobalDataIsUpdated(true));
        return { ok: true, value: { shopIds } };
    } catch (err) {
        store.dispatch(setIsLoading(false));
        toast.error(err instanceof Error ? err.message : String(err), {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
        return { ok: false, error: toError(err) };
    }
}

type ArchiveFidelityEvent = { selectedRow: FidelityRow };

export async function archiveFidelity(params: {
    selectedRows: FidelityRow[];
    e?: ArchiveFidelityEvent;
    shopsFidelityData: ShopsFidelityData;
    ctx: ArchiveContext;
}): Promise<Result<{ shopIds: string[] }>> {
    store.dispatch(setIsLoading(true));
    const { selectedRows, e, shopsFidelityData, ctx } = params;

    const data: Record<string, FidelityPayload> = {};
    const shopIds: string[] = [];

    if (selectedRows.length !== 0) {
        const arrayOfRows = getArrayOfRows(selectedRows, ctx.t);
        let list: FidelityRow[] = [];
        for (const el of arrayOfRows) {
            const associated = associateFidelity(el, shopsFidelityData);
            if (el.fidelityChoiceIndex === CESAR_LOYALTY) {
                mergeArchives(el, associated);
                list.push(el);
            } else {
                list = [...arrayOfRows, ...associated];
            }
        }
        for (let el of list) {
            el = JSON.parse(JSON.stringify(el));
            if (
                el.fidelityChoiceIndex === CESAR_LOYALTY &&
                Array.isArray(el.isArchived)
            ) {
                const index = el.isArchived.findIndex(
                    // eslint-disable-next-line no-loop-func
                    (shop: FidelityArchiveEntry) =>
                        shop.shopId === `${el.affectedShop}` ||
                        shop.shopId === el.affectedShop
                );
                if (index !== -1) {
                    el.isArchived[index].status = true;
                }
            }
            if (!shopIds.includes(el.shopId.toString())) {
                shopIds.push(el.shopId.toString());
            }
            data[el.Id] = buildFidelityPayload(el, ctx.t);
        }
        return submitArchive(ctx, data, shopIds);
    } else {
        if (e === undefined) {
            store.dispatch(setIsLoading(false));
            return { ok: false, error: new Error("No selection") };
        }
        const rowClone: FidelityRow = JSON.parse(JSON.stringify(e.selectedRow));
        const associated = associateFidelity(rowClone, shopsFidelityData);

        if (
            rowClone.fidelityChoiceIndex === CESAR_LOYALTY &&
            Array.isArray(rowClone.isArchived)
        ) {
            mergeArchives(rowClone, associated);
        }

        const franchiseShopId = rowClone.shopId.toString();
        if (!shopIds.includes(franchiseShopId)) {
            shopIds.push(franchiseShopId);
        }
        for (const associate of associated) {
            const associateShopId = associate.shopId.toString();
            if (!shopIds.includes(associateShopId)) {
                shopIds.push(associateShopId);
            }
        }

        data[rowClone.Id] = buildFidelityPayload(rowClone, ctx.t);
        return submitArchive(ctx, data, shopIds, { isarchived: true });
    }
}

type EditFidelityParams = {
    selectedShopsRows: FidelityRow[];
    values: FidelityValues;
    flags: FidelityFlags;
    setFlag: <K extends keyof FidelityFlags>(
        key: K,
        value: FidelityFlags[K]
    ) => void;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    t: TFunction;
};

export async function editFidelity(params: EditFidelityParams) {
    const {
        selectedShopsRows,
        values,
        flags,
        setFlag,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        t,
    } = params;

    toast.dismiss();
    const apiUrlUpdate = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;
    const row = selectedShopsRows[0];

    if (flags.isEdited === false) {
        store.dispatch(setIsConsultModalOpened(false));
        store.dispatch(setGlobalDataIsUpdated(true));
        return;
    }

    const payload: Partial<FidelityPayload> = {
        designation: values.designation,
        type: values.choiceFidelity,
        number_fidelity_points: values.numberFidelityPoints,
        money_correspondence: values.moneyCorrespondence,
        authentication_mode: values.authenticationMode.join(","),
        sortedVisibility: values.sortedVisibility,
        Login: values.login,
        password: values.password,
        note: values.remark,
        isActive: row?.isActive,
        isArchived: row?.isArchived,
        shopId: row.shopId.toString(),
        dashboardLink: values.dashboardLink,
        user: values.user,
        apiKey: values.apiKey,
        auresKonnectGroup: values.auresKonnectGroup,
        isCentralizationActive: flags.isCentralizationActive,
        isVisibiliteActive: flags.isVisibiliteActive,
        fidelityChoiceIndex: row?.fidelityChoiceIndex,
    };

    const dataConfig = {
        userId: userID,
        shopId: [row.shopId],
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser: uuidUser,
        data: { [row?.Id]: payload },
    };

    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataConfig),
    };

    try {
        const result: any = await fetchGlobal(apiUrlUpdate, requestOptions);
        if (result.error) {
            throw Error("error");
        }
        toast.success(
            `${t("The fidelity setting has been successfully modified")!}`,
            {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            }
        );

        setFlag("isEdited", false);
        store.dispatch(setIsConsultModalOpened(false));
        store.dispatch(setGlobalDataIsUpdated(true));
    } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error), {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    }
}

type FormValidityParams = {
    auresKonnectGroup: string;
    numberFidelityPoints: string;
    login: string;
    password: string;
    user: string;
    dashboardLink: string;
    apiKey: string;
    t: TFunction;
};

export function isFormValidForType(
    loyaltyType: string,
    params: FormValidityParams
): boolean {
    const {
        auresKonnectGroup,
        numberFidelityPoints,
        login,
        password,
        user,
        dashboardLink,
        apiKey,
        t,
    } = params;
    if (loyaltyType?.startsWith(t("Fidelity Softavera"))) {
        return (
            auresKonnectGroup !== "" &&
            !!numberFidelityPoints.match(numberMatchWord)
        );
    }
    if (loyaltyType === t("Fidelity Synapsy")) {
        return login !== "" && password !== "";
    }
    if (loyaltyType === t("Fidelity Zerosix")) {
        return (
            user !== "" &&
            password !== "" &&
            dashboardLink !== "" &&
            apiKey !== ""
        );
    }
    if (loyaltyType === t("Godia")) return true;
    return false;
}

type HandleValidateParams = EditFidelityParams & {
    setError: <K extends keyof FidelityErrors>(
        key: K,
        value: FidelityErrors[K]
    ) => void;
};

export function handleValidate(params: HandleValidateParams) {
    const { values, setError, selectedShopsRows, t } = params;

    if (values.designation === "") setError("designation", true);
    if (values.login === "") setError("login", true);
    if (values.password === "") setError("password", true);
    if (values.user === "") setError("user", true);
    if (values.numberFidelityPoints === "")
        setError("numberFidelityPoints", true);
    if (values.dashboardLink === "") setError("dashboardLink", true);
    if (values.apiKey === "") setError("apiKey", true);
    if (values.auresKonnectGroup === "") setError("group", true);

    if (
        selectedShopsRows[0]?.Id !== undefined &&
        isFormValidForType(selectedShopsRows[0][t("Loyalty")], {
            auresKonnectGroup: values.auresKonnectGroup,
            numberFidelityPoints: values.numberFidelityPoints,
            login: values.login,
            password: values.password,
            user: values.user,
            dashboardLink: values.dashboardLink,
            apiKey: values.apiKey,
            t,
        })
    ) {
        editFidelity(params);
    }
}
